import React, { useCallback, useMemo } from "react";
import { useMeetingStore } from "../../stores/useMeetingStore";
import useAxios, { UseAxiosProps } from "./useAxios";
import IMeeting from "../../api/IMeeting";
import { useActiveTopicStore } from "../../stores/useActiveTopicStore";
import { IRoundVote } from "../../api/IRound";
import useIsOpen from "../utils/useIsOpen";
import { toString } from "../../utilities/stringify";

type UseRoundControlProps = {
    axiosProps?: UseAxiosProps;
};

const useRoundControl = (params?: UseRoundControlProps) => {
    const meeting = useMeetingStore((state) => state.meeting);
    const { activeSubTopic, activeTask } = useActiveTopicStore(
        ({ activeSubTopic, activeTask }) => ({ activeSubTopic, activeTask })
    );

    const { error, loading, clearError, setError, post } = useAxios(
        params?.axiosProps
    );

    const [isCardShown, showCards, closeCards] = useIsOpen();

    const roundStarted =
        meeting?.votingRound !== undefined &&
        meeting.votingRound !== null &&
        meeting.votingRound.length > 0;

    // 判断是否正在显示投票结果
    const isShowingResults =
        !roundStarted &&
        meeting?.displayingRoundId !== undefined &&
        meeting.displayingRoundId !== null &&
        meeting.displayingRoundId.length > 0;

    // ?? useMemo ?? allParticipantsVoted ???
    const allParticipantsVoted = useMemo(() => {
        if (!meeting || meeting.participants.length === 0) {
            console.log("allParticipantsVoted: true (no participants)");
            return true;
        }

        // 只统计在线参会者，离线（已退出）的人不计入是否全部投票
        const onlineParticipants = meeting.participants.filter(
            participant => participant.isOnline !== false
        );

        if (onlineParticipants.length === 0) return true;

        const votedCount = onlineParticipants.filter(
            participant => participant.selectedPoker !== null && participant.selectedPoker !== undefined
        ).length;

        const result = votedCount === onlineParticipants.length;

        console.log(`allParticipantsVoted: ${result} (${votedCount}/${onlineParticipants.length} voted)`);
        
        // ?????????
        if (!result) {
            const notVoted = onlineParticipants
                .filter(p => !p.selectedPoker)
                .map(p => p.name);
            console.log("Participants not voted:", notVoted);
        }

        return result;
    }, [toString(meeting?.participants)]);

    const startRoundValid = () => {
        if (!meeting) return setError(`Missing meeting`);
        return true;
    };

    const endRoundValid = () => {
        if (!meeting) return setError(`Missing meeting`);
        return true;
    };

    const startRound = async () => {
        closeCards();

        if (!startRoundValid()) return;

        console.log("Starting voting round for topic:", activeTask?.id ?? activeSubTopic?.id);

        let res = await post<IMeeting>(
            `/api/meeting/StartVotingRound?meetingId=${meeting!.id}`,
            {
                votingOn: activeTask?.id ?? activeSubTopic?.id,
            }
        );

        if (res) {
            console.log("Voting round started successfully");
        } else {
            console.error("Failed to start voting round");
        }
    };

    const endRound = async () => {
        if (!endRoundValid()) return;

        closeCards();

        console.log("Ending voting round");

        const votes = meeting!.participants.map(
            (participant): IRoundVote => ({
                userId: participant.userId,
                value: participant.selectedPoker?.pokerValue,
            })
        );

        console.log("Collected votes:", votes);

        let res = await post<IMeeting>(
            `/api/meeting/EndVotingRound?meetingId=${meeting!.id}`,
            {
                meetingId: meeting!.id,
                votes: votes,
            }
        );

        if (res) {
            console.log("Voting round ended successfully");
        } else {
            console.error("Failed to end voting round");
        }
    };

    const onRoundClick = () => {
        if (!meeting) return;

        if (!roundStarted) {
            console.log("Starting new voting round");
            return startRound();
        }

        if (!isCardShown) {
            console.log("Showing cards");
            return showCards();
        }

        console.log("Ending voting round");
        endRound();
    };

    return {
        error,
        loading,
        roundStarted,
        isShowingResults,
        allParticipantsVoted,
        isCardShown,

        onRoundClick,
        clearError,
        setError,
    };
};

export default useRoundControl;
